import React from 'react';
import { motion } from 'framer-motion';

const MINISTRY_BANNER = 'https://static.prod-images.emergentagent.com/jobs/9bd4de2a-403c-4211-ba6a-7e7952c06d35/images/0f840a9da0fc7fdcf5f799cedc92d5d0049c007d763102e1d4304916e6d82131.png';

export default function MissionPage() {
  return (
    <div className="min-h-screen bg-cream pt-20" data-testid="mission-page">
      {/* Ministry Banner */}
      <div className="w-full">
        <img 
          src={MINISTRY_BANNER}
          alt="Living Fruit Ministry"
          className="w-full h-auto object-cover"
          style={{ maxHeight: '300px', objectPosition: 'center' }}
        />
      </div>

      {/* Mission Section */}
      <section className="py-16 md:py-24 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative w-full max-w-4xl mx-auto text-center rounded-2xl overflow-hidden"
          style={{ 
            background: '#fff9f2',
            boxShadow: '0 8px 48px rgba(160, 90, 40, 0.1)',
            padding: '80px 52px'
          }}
        >
          {/* Warm glow effect */}
          <div 
            className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-48 pointer-events-none"
            style={{
              background: 'radial-gradient(ellipse, rgba(222, 152, 80, 0.18), transparent 70%)'
            }}
          />

          {/* Leaf accent */}
          <div 
            className="absolute -bottom-5 -right-3 text-9xl opacity-5 -rotate-12 pointer-events-none select-none"
            style={{ fontSize: '180px' }}
          >
            🍃
          </div>

          {/* Tag */}
          <motion.span
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-block rounded-full mb-7"
            style={{
              background: '#f3e8d8',
              color: '#b06820',
              fontWeight: 600,
              fontSize: '0.7rem',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              padding: '6px 18px'
            }}
          >
            Our Mission
          </motion.span>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.1 }}
            className="font-serif mb-6"
            style={{
              fontSize: 'clamp(2.2rem, 5vw, 3.4rem)',
              fontWeight: 700,
              color: '#1A1A1A',
              lineHeight: 1.2
            }}
          >
            A Place to Grow,<br/>
            <em style={{ fontStyle: 'italic', color: '#c07030' }}>A Family to Belong To.</em>
          </motion.h1>

          {/* Divider */}
          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="flex items-center justify-center gap-3 mb-8"
          >
            <div 
              className="h-px w-12"
              style={{ background: 'linear-gradient(90deg, transparent, #d4956a)' }}
            />
            <span className="text-xl">🕊️</span>
            <div 
              className="h-px w-12"
              style={{ background: 'linear-gradient(90deg, #d4956a, transparent)' }}
            />
          </motion.div>

          {/* Mission Statement */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="max-w-2xl mx-auto mb-10"
            style={{
              fontSize: 'clamp(1.05rem, 2vw, 1.2rem)',
              fontWeight: 400,
              color: '#1E3A5F',
              lineHeight: 1.9
            }}
          >
            At Living Fruit Ministry, we believe every person is loved by God and has a purpose worth pursuing.
            We are a welcoming community committed to walking together in faith — growing spiritually,
            lifting one another up, and extending the love of Christ to every corner of Central Florida and beyond.
            You are not alone here. You are home.
          </motion.p>

          {/* Bible Verse */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.45 }}
            className="inline-flex items-start gap-3.5 text-left rounded-r-lg max-w-xl mx-auto"
            style={{
              background: '#f7ede0',
              borderLeft: '3px solid #d4956a',
              padding: '16px 24px'
            }}
          >
            <span className="text-xl mt-0.5 flex-shrink-0">📖</span>
            <p style={{ color: '#7a4a28', lineHeight: 1.6 }}>
              <strong 
                className="block mb-1"
                style={{
                  fontFamily: 'inherit',
                  fontStyle: 'normal',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  letterSpacing: '0.15em',
                  textTransform: 'uppercase',
                  color: '#b06820'
                }}
              >
                John 15:16
              </strong>
              <span className="font-serif italic" style={{ fontSize: '0.98rem' }}>
                "I chose you and appointed you so that you might go and bear fruit — fruit that will last."
              </span>
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Additional Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid md:grid-cols-3 gap-8"
          >
            {/* Vision */}
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#f3e8d8' }}>
                <span className="text-2xl">👁️</span>
              </div>
              <h3 className="font-serif text-xl mb-3" style={{ color: '#1A1A1A' }}>Our Vision</h3>
              <p style={{ color: '#1E3A5F', lineHeight: 1.7 }}>
                To see lives transformed by the love of Christ, creating a ripple effect of hope throughout Central Florida.
              </p>
            </div>

            {/* Values */}
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#f3e8d8' }}>
                <span className="text-2xl">💛</span>
              </div>
              <h3 className="font-serif text-xl mb-3" style={{ color: '#1A1A1A' }}>Our Values</h3>
              <p style={{ color: '#1E3A5F', lineHeight: 1.7 }}>
                Faith, Family, Service, Community, and unconditional Love for all who seek a place to belong.
              </p>
            </div>

            {/* Purpose */}
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: '#f3e8d8' }}>
                <span className="text-2xl">🌱</span>
              </div>
              <h3 className="font-serif text-xl mb-3" style={{ color: '#1A1A1A' }}>Our Purpose</h3>
              <p style={{ color: '#1E3A5F', lineHeight: 1.7 }}>
                To nurture spiritual growth, provide for those in need, and build a community rooted in God's word.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16" style={{ background: '#1A1A1A' }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Join Our Family
            </h2>
            <p className="text-white/80 mb-8 max-w-xl mx-auto">
              Whether you're taking your first steps in faith or looking for a community to call home, 
              we welcome you with open arms.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a 
                href="/im-new"
                className="px-8 py-3 rounded-full font-medium transition-all hover:scale-105"
                style={{ background: '#c07030', color: 'white' }}
              >
                I'm New Here
              </a>
              <a 
                href="/contact"
                className="px-8 py-3 rounded-full font-medium transition-all hover:scale-105 border-2"
                style={{ borderColor: '#c07030', color: '#c07030', background: 'transparent' }}
              >
                Get In Touch
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
